import React, { useEffect, useRef, useState } from 'react';
import { useGetCurrentOwner } from '../../hooks/apolloHooks/useGetCurrentOwner';
import { useGetCurrentUserProfile } from '../../hooks/apolloHooks/useGetCurrentUserProfile';
import { useGetCustomer } from '../../hooks/apolloHooks/useGetCustomer';
import { useGetOwnerCustomerRoles } from '../../hooks/apolloHooks/useGetOwnerCustomerRoles';
import { GET_AUTOGIRO_DATA_QUERY, GET_CUSTOMER_AGREEMENTS_QUERY, GET_CUSTOMER_RIGHTS_DATA_QUERY } from '../../integrations/allkort/graphql/gql/CustomerQueries';
import { AutogiroDataResponse, CustomerAgreementsResponse, CustomerRightsDataResponse } from '../../integrations/allkort/graphql/interface/Customer';
import { TokenService } from '../../services/TokenService';

type DebugInfoProps = {
    wrapperClasses?: string

}

const DebugInfo: React.FC<DebugInfoProps> = ({ wrapperClasses }) => {
    const dialogRef = useRef<HTMLDialogElement | null>(null);
    const [inputCustomerNumber, setInputCustomerNumber] = useState('');
    const [isUnlocked, setIsUnlocked] = useState(false);
    const [unlockError, setUnlockError] = useState('');

    useEffect(() => {
        const dialog = dialogRef.current;
        if (!dialog) return;

        if (typeof dialog.showModal === 'function' && !dialog.open) {
            dialog.showModal();
        } else {
            dialog.setAttribute('open', '');
        }

        return () => {
            if (dialog.open) {
                dialog.close();
            }
        };
    }, []);

    const { owner, error } = useGetCurrentOwner()
    if (error) console.error('Error fetching owner data:', error.message)
    const { data: currentCustomerData, error: currentCustomerError } = useGetCustomer<CustomerRightsDataResponse>(
        GET_CUSTOMER_RIGHTS_DATA_QUERY,
        {},
        { fetchPolicy: 'network-only' }
    );
    const { data: autogiroData, loading: autogiroLoading, error: autogiroError } = useGetCustomer<AutogiroDataResponse>(
        GET_AUTOGIRO_DATA_QUERY,
        {},
        { fetchPolicy: 'network-only' }
    );

    const token = TokenService.getToken();
    const tokenExpiry = TokenService.getTokenExpiry();
    const refreshToken = TokenService.getRefreshToken();
    const refreshTokenExpiry = TokenService.getRefreshTokenExpiry();

    // Get the current user's profile and roles
    const { userProfile, loading: userProfileLoading, error: userProfileError } = useGetCurrentUserProfile();
    const claimNames = userProfile?.roles?.map(role => role.claimName) ?? [];

    // Get all available rights from owner customer roles
    const { roles, loading: rolesLoading, isComplete: rolesComplete } = useGetOwnerCustomerRoles();

    const { data: agreementsData, loading: agreementsLoading, error: agreementsError } = useGetCustomer<CustomerAgreementsResponse>(
        GET_CUSTOMER_AGREEMENTS_QUERY,
        { first: 15, after: null, before: null, last: null }
    );

    const handleUnlockSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const expectedCustomerNumber = currentCustomerData?.currentCustomer?.customerNumber;
        if (!expectedCustomerNumber) {
            setUnlockError('Service code could not be fetched yet. Please try again.');
            return;
        }

        if (inputCustomerNumber.trim() === String(expectedCustomerNumber)) {
            setIsUnlocked(true);
            setUnlockError('');
            return;
        }

        setIsUnlocked(false);
        setUnlockError('Incorrect service code  .');
    };

    return (
        <div className={`debug-component ${wrapperClasses ?? ''}`}>
            <dialog ref={dialogRef}>
                <h6>Debug info</h6>
                <div className="debug-content">
                    {!isUnlocked && (
                        <form onSubmit={handleUnlockSubmit}>
                            <label htmlFor="debug-service-code">
                                Service code
                            </label>
                            <input
                                id="debug-service-code"
                                type="text"
                                value={inputCustomerNumber}
                                onChange={e => setInputCustomerNumber(e.target.value)}
                                placeholder="Service code"
                            />
                            <button type="submit" className="Button body-text">
                                Show debug info
                            </button>
                            {unlockError && <p>{unlockError}</p>}
                        </form>
                    )}

                    {isUnlocked && (
                        <>
                            <span>
                                <strong>Matomo ID:</strong>
                                {import.meta.env.VITE_MATOMO_CONTAINER_ID ? import.meta.env.VITE_MATOMO_CONTAINER_ID : 'No Matomo container ID configured'}
                            </span>
                            <span>
                                <strong>Allkort URL:</strong>
                                {import.meta.env.VITE_GRAPHQL_ENDPOINT ? import.meta.env.VITE_GRAPHQL_ENDPOINT : 'No Allkort URL configured'}
                            </span>
                            <span>
                                <strong>Plusgiro:</strong>
                                {owner?.publicFinancialDetails?.invoicePlusgiro}
                            </span>

                            <span>
                                <strong>Bankgiro:</strong>
                                {owner?.publicFinancialDetails?.invoiceBankgiro}
                            </span>
                            <span>
                                <strong>Frozen:</strong>
                                {currentCustomerData?.currentCustomer?.frozen?.is ? 'Yes' : 'No'}
                            </span>
                            <span>
                                <strong>State:</strong>
                                {currentCustomerData?.currentCustomer?.state
                                    ? `${currentCustomerData.currentCustomer.state.name} (${currentCustomerData.currentCustomer.state.id})`
                                    : 'N/A'}
                            </span>
                            <span>
                                <strong>Token:</strong>
                                <details>
                                    <summary>Show token</summary>
                                    <ul>
                                        <li>
                                            <strong>Access token:</strong>
                                            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{token ?? 'N/A'}</pre>
                                        </li>
                                        <li>
                                            <strong>Access token expiry:</strong> {tokenExpiry ? tokenExpiry.toISOString() : 'N/A'}
                                        </li>
                                        <li>
                                            <strong>Refresh token:</strong> {refreshToken ? 'Present' : 'Missing'}
                                        </li>
                                        <li>
                                            <strong>Refresh token expiry:</strong> {refreshTokenExpiry ? refreshTokenExpiry.toISOString() : 'N/A'}
                                        </li>
                                    </ul>
                                </details>
                            </span>
                            <span>
                                <strong>Current user roles:</strong>
                                <details>
                                    <summary>Show claimNames ({claimNames.length})</summary>
                                    {userProfileLoading ? (
                                        <p>Laddar roller...</p>
                                    ) : userProfileError ? (
                                        <p>Kunde inte hämta roller.</p>
                                    ) : (
                                        <ul>
                                            {claimNames.map((claimName, index) => (
                                                <li key={`${claimName}-${index}`}>{claimName}</li>
                                            ))}
                                        </ul>
                                    )}
                                </details>
                            </span>

                            <span>
                                <strong>Autogiro:</strong>
                                <details>
                                    <summary>Visa autogiro</summary>
                                    {autogiroLoading ? (
                                        <p>Laddar autogiro...</p>
                                    ) : autogiroError ? (
                                        <p>Kunde inte hämta autogiro.</p>
                                    ) : (
                                        <ul>
                                            <li>Approved: {autogiroData?.currentCustomer?.autogiro?.approved ? 'Yes' : 'No'}</li>
                                            <li>Clearing number: {autogiroData?.currentCustomer?.autogiro?.clearingNumber ?? 'N/A'}</li>
                                            <li>Account number: {autogiroData?.currentCustomer?.autogiro?.accountNumber ?? 'N/A'}</li>
                                            <li>Change requested: {autogiroData?.currentCustomer?.autogiro?.changeRequested ? 'Yes' : 'No'}</li>
                                            <li>Change requested at: {autogiroData?.currentCustomer?.autogiro?.changeRequestedAt ?? 'N/A'}</li>
                                        </ul>
                                    )}
                                </details>
                            </span>

                            <span>
                                <strong>All Available rights:</strong>
                                <details>
                                    <summary>Visa tillgängliga i allkort rättigheter</summary>
                                    {rolesLoading ? (
                                        <p>Laddar tillgängliga rättigheter...</p>
                                    ) : !rolesComplete ? (
                                        <p>Hämtar fler rättigheter...</p>
                                    ) : roles.length === 0 ? (
                                        <p>Inga rättigheter hittades.</p>
                                    ) : (
                                        <ul>
                                            {roles.map((role, index) => (
                                                <li key={`${role.claimName}-${index}`}>{role.claimName}</li>
                                            ))}
                                        </ul>
                                    )}
                                </details>
                            </span>

                            <span>
                                <strong>Agreements:</strong>
                                {agreementsLoading ? (
                                    <p>Laddar avtal...</p>
                                ) : agreementsError ? (
                                    <p>Kunde inte hämta avtal.</p>
                                ) : agreementsData?.currentCustomer?.agreementApprovals?.nodes.length ? (
                                    <details>
                                        <summary>Visa avtal ({agreementsData.currentCustomer.agreementApprovals.nodes.length})</summary>
                                        <ul>
                                            {agreementsData.currentCustomer.agreementApprovals.nodes.map((approval, index) => (
                                                <li key={`${approval.agreement.id}-${index}`}>
                                                    {approval.agreement.name} - Updated at: {approval.updatedAt}
                                                </li>
                                            ))}
                                        </ul>
                                    </details>
                                ) : (
                                    'Inga avtal hittades.'
                                )}
                            </span>

                            <button style={{ marginTop: '2rem' }} onClick={() => dialogRef.current?.close()} className="Button button-cancel">
                                Close
                            </button>
                        </>
                    )}
                </div>
            </dialog>

        </div>
    )
}

export default DebugInfo;